/**
 * Firebase Configuration for Tennis Team Manager
 *
 * This module initializes and exports Firebase services for authentication.
 * It's designed to be modular and extensible for adding additional auth providers.
 *
 * Configuration:
 * --------------
 * Set the following environment variables in your .env file:
 * - REACT_APP_FIREBASE_API_KEY: Your Firebase API key
 * - REACT_APP_FIREBASE_AUTH_DOMAIN: Your Firebase auth domain
 * - REACT_APP_FIREBASE_PROJECT_ID: Your Firebase project ID
 * - REACT_APP_FIREBASE_STORAGE_BUCKET: Your Firebase storage bucket
 * - REACT_APP_FIREBASE_MESSAGING_SENDER_ID: Your Firebase messaging sender ID
 * - REACT_APP_FIREBASE_APP_ID: Your Firebase app ID
 *
 * To get these values:
 * 1. Go to Firebase Console (https://console.firebase.google.com/)
 * 2. Select your project (or create a new one)
 * 3. Go to Project Settings -> General
 * 4. Scroll down to "Your apps" and click "Add app" -> Web
 * 5. Copy the configuration values
 *
 * Extending for Additional Auth Providers:
 * ----------------------------------------
 * This configuration supports adding additional auth providers:
 *
 * 1. Google Sign-In:
 *    - Enable Google Sign-In in Firebase Console -> Authentication -> Sign-in method
 *    - Use: import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
 *    - ON iOS (Capacitor) THIS DOES NOT WORK — see "Native sign-in" below.
 *
 * 2. Sign in with Apple:
 *    - Enable Apple in Firebase Console -> Authentication -> Sign-in method
 *    - Required by App Store guideline 4.8 in any build that offers Google sign-in
 *    - Offered only in the native iOS build today; enabling it on the web additionally
 *      needs an Apple Services ID + signing key registered in the Firebase console
 *
 * 3. Facebook Login:
 *    - Enable Facebook in Firebase Console -> Authentication -> Sign-in method
 *    - Add your Facebook App ID and Secret
 *    - Use: import { FacebookAuthProvider, signInWithPopup } from 'firebase/auth';
 *
 * 4. Other providers (Twitter, GitHub, etc.):
 *    - Enable in Firebase Console
 *    - Import corresponding provider from 'firebase/auth'
 *
 * Native sign-in (iOS / Capacitor):
 * ---------------------------------
 * signInWithPopup cannot open a popup inside a WKWebView, and signInWithRedirect is no
 * fallback there either: it leaves the app origin, capacitor://localhost can never be an
 * authorized Firebase redirect URI, and Google blocks embedded webviews outright
 * (disallowed_useragent). FirebaseLogin.tsx therefore drives the native account sheet via
 * @capacitor-firebase/authentication with `skipNativeAuth: true`, which returns the raw
 * OAuth credential instead of signing into the native Firebase SDK. The sign-in is then
 * completed here in the web SDK with signInWithCredential(), so `auth` below stays the one
 * source of truth for currentUser, getIdToken() and onAuthStateChanged on every platform.
 *
 * Security Notes:
 * ---------------
 * - API keys for Firebase Web apps are public (they identify your app, not authenticate)
 * - Security is enforced through Firebase Security Rules
 * - Never expose your Firebase Admin credentials (used only on backend)
 */

import { initializeApp, FirebaseApp, getApps } from 'firebase/app';
import {
  getAuth,
  Auth,
  connectAuthEmulator,
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
};

/**
 * Clean up any stale Firebase redirect state from sessionStorage.
 * This prevents "missing initial state" errors when Firebase tries to handle
 * redirect flows that were started in a different session or tab.
 */
export const cleanupFirebaseRedirectState = (): void => {
  try {
    // Clear any Firebase redirect-related sessionStorage items
    // These can cause "missing initial state" errors if they're stale.
    // Preserve keys that track an in-flight redirect — wiping them would
    // break the signInWithRedirect round-trip that we use as a fallback
    // when popups are blocked.
    const isInFlightRedirectKey = (key: string): boolean =>
      key.startsWith('firebase:pendingRedirect:') ||
      key.startsWith('firebase:redirectEvent:');

    const firebaseKeys = Object.keys(sessionStorage).filter(key =>
      (key.startsWith('firebase:') ||
        key.startsWith('firebaseui::') ||
        key.includes('firebase')) &&
      !isInFlightRedirectKey(key)
    );
    
    firebaseKeys.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        // Ignore errors when clearing storage
      }
    });
    
    // Also clear any Firebase redirect state from localStorage that might be stale
    const firebaseLocalKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('firebase:redirectUser:')
    );
    
    firebaseLocalKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore errors when clearing storage
      }
    });
  } catch (error) {
    // Silently fail - this is a cleanup operation
    console.warn('Failed to cleanup Firebase redirect state:', error);
  }
};

// Initialize Firebase only if configured
let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (isFirebaseConfigured()) {
  // Clean up any stale Firebase redirect state before initialization
  // This prevents "missing initial state" errors
  cleanupFirebaseRedirectState();
  
  // Check if Firebase app already exists to avoid re-initialization
  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }

  try {
    auth = getAuth(app);

    // Connect to Auth Emulator in development (if configured)
    if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST) {
      connectAuthEmulator(auth, `http://${process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST}`);
    }

    // Listen for auth state changes to detect redirect state issues
    // Note: We don't set up a global listener here as it would interfere with component-level listeners
    // The error handling is done at the component level where auth operations occur
  } catch (error: any) {
    console.error('Firebase auth initialization error:', error);
  }
} else {
  console.warn('Firebase is not configured. Set REACT_APP_FIREBASE_* environment variables to enable Firebase authentication.');
}

// Suppress orphan unhandled rejections from Firebase Auth's internal
// NetworkTimeout race. These fire when a transient connection blip occurs
// (tab backgrounded, weak network) and have no app frames in the stack
// because they originate from Firebase's internal Promise.race losers.
// User-facing auth flows already catch and display auth/network-request-failed
// at every call site, so this is pure noise in error reporting.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { code?: string; name?: string } | undefined;
    if (
      reason &&
      reason.name === 'FirebaseError' &&
      reason.code === 'auth/network-request-failed'
    ) {
      event.preventDefault();
    }
  });
}

// Export Firebase app and auth instances
export { app, auth };

// Export for backwards compatibility and direct usage
export default app;
